import { ref } from "vue";
import type { Ref } from "vue";
import { autoRef } from "~/utils/autoRef";
import type { E1140Response } from "@r3shared/types/WMS/MODU_COLETOR";
import type { Tabs } from "~/pages/PROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR.vue";
import { RecebimentoTabClass } from "~/classes/PROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR/RecebimentoTabClass";
import { UMAPaleteConferidoTabClass } from "../conferenciaDePlanilhaCega/UMAPaleteConferidoTab";
import type { DadosLogisticosTabClass } from "../conferenciaDePlanilhaCega/DadosLogisticosTab";
import type { TaraTabClass } from "./TaraTabClass";
// Imports e refs entre os marcadores são gerados pelo watcher
// (backend/src/watchers/componentClassesWatcher.ts) a partir do template
// do .vue correspondente. Tudo FORA dos blocos é preservado verbatim.
/* inicio imports gerados — AUTO-GERADO, não edite */
import type { R3DataFinderClass } from "~/classes/base/R3DataFinder.classes";
import type { R3InputNumberClass } from "~/classes/base/R3InputBase.classes";
import type { R3TabPanelClass } from "../base/R3TabPanel.classes";
/* fim imports gerados */

/**
 * Lembrar:
 *
 * Quando for usar uma classe base de uma tela ou componente que não tenha o defineExpose,
 * o autoRef serve para "Desembrulhar" o Ref e pegar a classe diretamente. Se for feito onde não há
 * o defineExpose, já vai ter "Desembrulhado", quando for tentar obter o valor do getter, vai haver erro
 * uma vez que já não existe mais o Ref e sim o valor direto.
 */

export class FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR {
	/* inicio refs — AUTO-GERADO, não edite */
	protocoloDataFinderRef = autoRef<R3DataFinderClass>(null);
	conferenciaTabPanelRef = ref<R3TabPanelClass>(null);
	conferenciaTabTaraRef: Ref<unknown | null> = ref(null);
	conferenciaDadosLogisticosRef: Ref<unknown | null> = ref(null);
	protocoloInput = autoRef<R3InputNumberClass>(null);
	/* fim refs */

	protocoloDescricao = autoRef<string>("Protocolo");
	recebimentoTab: RecebimentoTabClass = new RecebimentoTabClass();

	UMAValidada = ref<boolean>(false);
	/**
	 * Tipo escolhido ao registrar a U.M.A. da B315.
	 * A decisão precisa sobreviver à continuação automática da conferência,
	 * sem obrigar o coletor a voltar ao botão Conf para escolhê-la novamente.
	 */
	tipoMovimentacaoUMA = ref<number | null>(null);
	UMAPaleteConferidoTab = new UMAPaleteConferidoTabClass();
	dadosLogisticosTab = autoRef<DadosLogisticosTabClass>(null);
	taraTab = autoRef<TaraTabClass>(null);
	protocolo = autoRef<E1140Response>(null);
	chamadoB315 = ref<boolean>(false);

	activeTab = ref<Tabs>("recebimento");

	/**
	 * Muda a tab ativa no momento
	 * @param tab - Tab a ser ativada
	 */
	changeActiveTab = (tab: Tabs) => {
		this.activeTab.value = tab;
		this.conferenciaTabPanelRef.value.setTabFocusByName(tab);
	};

	/**
	 * Abas no estilo Delphi (PageControlGeral).
	 * Fixas com protocolo aberto: Recebimento, Impressora Direcionada, Calc peso var.
	 * Conferidas/Pendentes conforme lista; Tara / D.Logist conforme mercadoria;
	 * U.M.A só B315.
	 */
	panelTabs = autoRef([
		{
			name: "recebimento",
			header: "Recebimento",
			visible: true,
		},
		{
			name: "conferidas",
			header: "Conferidas",
			visible: false,
		},
		{
			name: "pendentes",
			header: "Pendentes",
			visible: false,
		},
		{
			name: "tara",
			header: "Tara",
			visible: false,
		},
		{
			name: "dadosLogisticos",
			header: "D.Logist",
			visible: false,
		},
		{
			name: "impressoraDirecionada",
			header: "Impressora Direcionada",
			visible: true,
		},
		{
			name: "calcPesoVar",
			header: "Calc peso var",
			visible: true,
		},
		{
			name: "UMAPaleteConferido",
			header: "U.M.A por Palete Conferido",
			visible: false,
		},
	]);

	/**
	 * Atualiza `TabVisible` de uma aba (Delphi) e reatribui o array para o
	 * R3TabPanel filtrar de novo.
	 */
	setPanelTabVisible(name: string, visible: boolean) {
		const tabsRef = this.panelTabs as unknown as {
			value?: Array<{ name: string; header: string; visible: boolean }>;
			filter?: unknown;
			length?: number;
		};
		let lista: Array<{ name: string; header: string; visible: boolean }> = [];
		if (Array.isArray(tabsRef.value)) {
			lista = tabsRef.value;
		}
		else if (Array.isArray(this.panelTabs)) {
			lista = this.panelTabs as unknown as Array<{ name: string; header: string; visible: boolean }>;
		}
		else if (typeof tabsRef.filter === "function" && typeof tabsRef.length === "number") {
			// autoRef proxy (array-like)
			lista = Array.from(tabsRef as unknown as ArrayLike<{ name: string; header: string; visible: boolean }>);
		}
		const proxima = lista.map(tab =>
			tab.name === name ? { ...tab, visible } : { ...tab },
		);
		// autoRef: atribuição em .value atualiza o Ref interno
		if (tabsRef.value !== undefined || typeof tabsRef.filter === "function") {
			tabsRef.value = proxima;
		}
	}
}
